import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDayInTz, endOfDayInTz, formatInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { calculateConversionRate } from '@/utils/dateFieldStandards';
import { fetchAllRows } from '@/utils/fetchAllRows';

interface CampaignMetrics {
  campaign_id: string;
  campaign_name: string;
  total_leads: number;
  leads_converted: number;
  conversion_rate: number;
  total_bookings: number;
  jobs_completed: number;
  jobs_cancelled: number;
  jobs_quoted: number;
  total_revenue: number;
  actual_cost: number;
  cost_per_acquisition: number;
  return_on_ad_spend: number;
  campaign_profit: number;
}

interface LeadSourceWithCampaigns {
  source_id: string;
  source_name: string;
  is_active: boolean;
  total_leads: number;
  leads_converted: number;
  conversion_rate: number;
  total_bookings: number;
  jobs_completed: number;
  jobs_cancelled: number;
  jobs_quoted: number;
  total_revenue: number;
  actual_cost: number;
  cost_per_acquisition: number;
  return_on_ad_spend: number;
  campaign_count: number;
  campaigns: CampaignMetrics[];
  campaign_profit: number;
}

export const useLeadSourceWithCampaigns = (startDate: Date, endDate: Date, timezone: string = DEFAULT_TIMEZONE) => {
  return useQuery({
    queryKey: ['lead-source-with-campaigns', formatInTz(startDate, timezone, 'yyyy-MM-dd'), formatInTz(endDate, timezone, 'yyyy-MM-dd'), timezone],
    queryFn: async () => {
      const period = formatInTz(new Date(startDate.getFullYear(), startDate.getMonth(), 1), timezone, 'yyyy-MM-dd');

      // All sources and campaigns, active or not: a campaign paused since still owns
      // the leads and spend it had in the period.
      const { data: sources, error: sourcesError } = await supabase
        .from('lead_sources')
        .select('id, name, is_active')
        .order('name');

      if (sourcesError) throw sourcesError;

      const { data: campaigns, error: campaignsError } = await supabase
        .from('campaigns')
        .select('id, name, lead_source_id, is_active');

      if (campaignsError) throw campaignsError;

      // Get daily spend
      const dailySpend = await fetchAllRows((from, to) => supabase
        .from('campaign_daily_spend')
        .select('campaign_id, actual_cost')
        .gte('spend_date', formatInTz(startDate, timezone, 'yyyy-MM-dd'))
        .lte('spend_date', formatInTz(endDate, timezone, 'yyyy-MM-dd'))
        .order('id')
        .range(from, to));

      // Get leads
      const leads = await fetchAllRows((from, to) => supabase
        .from('leads')
        .select('id, campaign_id, status')
        .gte('created_at', startOfDayInTz(startDate, timezone).toISOString())
        .lte('created_at', endOfDayInTz(endDate, timezone).toISOString())
        .order('id')
        .range(from, to));

      // Get all jobs (not just completed) so we can break down booked / completed / cancelled
      const jobs = await fetchAllRows((from, to) => supabase
        .rpc('get_jobs_for_leads_in_range', {
          start_ts: startOfDayInTz(startDate, timezone).toISOString(),
          end_ts: endOfDayInTz(endDate, timezone).toISOString()
        })
        .order('id')
        .range(from, to));

      // Build spend map
      const campaignSpendMap = new Map<string, number>();
      dailySpend.forEach(ds => {
        const existing = campaignSpendMap.get(ds.campaign_id) || 0;
        campaignSpendMap.set(ds.campaign_id, existing + (ds.actual_cost || 0));
      });

      // Leads with no campaign, or whose campaign has no lead source, still count:
      // they land in an Unattributed row so the total matches the leads list.
      const campaignById = new Map((campaigns || []).map(c => [c.id, c]));
      const UNATTRIBUTED = 'unattributed';
      const NO_CAMPAIGN = 'no-campaign';
      const bucketOf = (campaignId: string | null) =>
        campaignId && campaignById.has(campaignId) ? campaignId : NO_CAMPAIGN;

      const leadsByCampaign = new Map<string, typeof leads>();
      const campaignByLeadId = new Map<string, string>();
      leads.forEach(l => {
        const key = bucketOf(l.campaign_id);
        leadsByCampaign.set(key, [...(leadsByCampaign.get(key) || []), l]);
        campaignByLeadId.set(l.id, key);
      });
      const jobsByCampaign = new Map<string, typeof jobs>();
      jobs.forEach(j => {
        const key = campaignByLeadId.get(j.lead_id);
        if (key) jobsByCampaign.set(key, [...(jobsByCampaign.get(key) || []), j]);
      });

      const campaignRows = [
        ...(campaigns || []),
        { id: NO_CAMPAIGN, name: 'No campaign', lead_source_id: null, is_active: false },
      ].filter(c => c.is_active || leadsByCampaign.has(c.id) || campaignSpendMap.has(c.id));

      // Build metrics per campaign
      const campaignMetrics = new Map<string, CampaignMetrics>();
      campaignRows.forEach(campaign => {
        const campaignLeads = leadsByCampaign.get(campaign.id) || [];
        const convertedLeads = campaignLeads.filter(l => l.status === 'converted');
        const allCampaignJobs = jobsByCampaign.get(campaign.id) || [];
        const completedJobs = allCampaignJobs.filter(j => j.status === 'completed');
        const cancelledJobs = allCampaignJobs.filter(j => j.status === 'cancelled');
        const quotedJobs = allCampaignJobs.filter(j => j.status === 'quote_sent');
        const totalRevenue = completedJobs.reduce((sum, j) => sum + (j.invoice_total || 0), 0);
        const actualCost = campaignSpendMap.get(campaign.id) || 0;

        const conversionRate = calculateConversionRate(convertedLeads.length, campaignLeads.length);
        const costPerAcquisition = actualCost > 0 && convertedLeads.length > 0
          ? actualCost / convertedLeads.length
          : 0;
        const returnOnAdSpend = actualCost > 0 ? totalRevenue / actualCost : 0;
        const jobProfit = completedJobs.reduce((sum, j) =>
          sum + ((j.company_profit || 0) - (j.total_override_commission || 0)), 0
        );
        const campaignProfit = jobProfit - actualCost;

        campaignMetrics.set(campaign.id, {
          campaign_id: campaign.id,
          campaign_name: campaign.name,
          total_leads: campaignLeads.length,
          leads_converted: convertedLeads.length,
          conversion_rate: conversionRate,
          total_bookings: allCampaignJobs.length,
          jobs_completed: completedJobs.length,
          jobs_cancelled: cancelledJobs.length,
          jobs_quoted: quotedJobs.length,
          total_revenue: totalRevenue,
          actual_cost: actualCost,
          cost_per_acquisition: costPerAcquisition,
          return_on_ad_spend: returnOnAdSpend,
          campaign_profit: campaignProfit,
        });
      });

      // Group campaigns by source
      const sourceIds = new Set((sources || []).map(s => s.id));
      const campaignsBySource = new Map<string, string[]>();
      campaignRows.forEach(c => {
        const sourceId = c.lead_source_id && sourceIds.has(c.lead_source_id) ? c.lead_source_id : UNATTRIBUTED;
        campaignsBySource.set(sourceId, [...(campaignsBySource.get(sourceId) || []), c.id]);
      });

      // Build source metrics with campaigns (an inactive source still shows if it had activity)
      const metrics: LeadSourceWithCampaigns[] = [
        ...(sources || []),
        { id: UNATTRIBUTED, name: 'Unattributed', is_active: false },
      ]
        .filter(source => {
          const sourceCampaignIds = campaignsBySource.get(source.id) || [];
          return sourceCampaignIds.length > 0 && (source.is_active
            || sourceCampaignIds.some(id => leadsByCampaign.has(id) || campaignSpendMap.has(id)));
        })
        .map(source => {
          const sourceCampaignIds = campaignsBySource.get(source.id) || [];
          const sourceCampaigns = sourceCampaignIds
            .map(id => campaignMetrics.get(id))
            .filter((c): c is CampaignMetrics => c !== undefined)
            .sort((a, b) => b.total_revenue - a.total_revenue);

          const totalLeads = sourceCampaigns.reduce((sum, c) => sum + c.total_leads, 0);
          const leadsConverted = sourceCampaigns.reduce((sum, c) => sum + c.leads_converted, 0);
          const totalBookings = sourceCampaigns.reduce((sum, c) => sum + c.total_bookings, 0);
          const jobsCompleted = sourceCampaigns.reduce((sum, c) => sum + c.jobs_completed, 0);
          const jobsCancelled = sourceCampaigns.reduce((sum, c) => sum + c.jobs_cancelled, 0);
          const jobsQuoted = sourceCampaigns.reduce((sum, c) => sum + c.jobs_quoted, 0);
          const totalRevenue = sourceCampaigns.reduce((sum, c) => sum + c.total_revenue, 0);
          const actualCost = sourceCampaigns.reduce((sum, c) => sum + c.actual_cost, 0);
          const campaignProfit = sourceCampaigns.reduce((sum, c) => sum + c.campaign_profit, 0);

          const conversionRate = calculateConversionRate(leadsConverted, totalLeads);
          const costPerAcquisition = actualCost > 0 && leadsConverted > 0 ? actualCost / leadsConverted : 0;
          const returnOnAdSpend = actualCost > 0 ? totalRevenue / actualCost : 0;

          return {
            source_id: source.id,
            source_name: source.name,
            is_active: source.is_active,
            total_leads: totalLeads,
            leads_converted: leadsConverted,
            conversion_rate: conversionRate,
            total_bookings: totalBookings,
            jobs_completed: jobsCompleted,
            jobs_cancelled: jobsCancelled,
            jobs_quoted: jobsQuoted,
            total_revenue: totalRevenue,
            actual_cost: actualCost,
            cost_per_acquisition: costPerAcquisition,
            return_on_ad_spend: returnOnAdSpend,
            campaign_count: sourceCampaigns.length,
            campaigns: sourceCampaigns,
            campaign_profit: campaignProfit,
          };
        })
        .sort((a, b) => b.total_revenue - a.total_revenue);

      return metrics;
    },
    staleTime: 5 * 60 * 1000,
  });
};
